import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  addManageClubManager,
  createManageEvent,
  deleteManageEvent,
  listClubEvents,
  listManageTracks,
  updateManageEvent,
  type ManageEventInput,
  listManageClubManagers,
  listMyClubs,
  removeManageClubManager,
  type ClubManagerRow,
  type ClubRole,
  type ClubRow,
  type EventRow,
} from "../lib/api";
import { formatEventDate } from "../lib/formatDate";
import { useSiteClub } from "../lib/SiteClubContext";
import { Trash3FillIcon } from "../components/icons";
import Toast from "../components/Toast";
import { ManageClubNav } from "../components/ManageClubNav";
import { ManageCreditsPanel } from "../components/ManageCreditsPanel";
import { cleanEventForm, EventForm, useClassOptions } from "../components/EventForm";

// Manage section for club and event managers (admins see every club).
// Step one of moving toward multi-club: pick a club, see its events.
// Managers create, edit and delete their club's events here; the club comes
// from the URL, so an event can't be filed under someone else's club.

/** /manage -- which club? Skipped straight to the club when there's only
 *  one, or when this is a club's own domain and you manage that club. */
export function ManageClubsPage() {
  const { club: siteClub } = useSiteClub();
  const [clubs, setClubs] = useState<ClubRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listMyClubs()
      .then((res) => setClubs(res.clubs))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load clubs."));
  }, []);

  if (error) return <p className="err">{error}</p>;
  if (!clubs) return <p>Loading…</p>;
  if (siteClub && clubs.some((c) => c.clubId === siteClub.clubId)) {
    return <Navigate to={`/manage/${siteClub.clubId}`} replace />;
  }
  if (clubs.length === 1) return <Navigate to={`/manage/${clubs[0].clubId}`} replace />;

  return (
    <div>
      <h1>Manage</h1>
      {clubs.length === 0 ? (
        <p className="notice">You aren't a manager of any club yet. A site admin can add you.</p>
      ) : (
        <>
          <p className="muted">Pick the club you want to manage.</p>
          <div className="events-grid single-column">
            {clubs.map((c) => (
              <Link key={c.clubId} to={`/manage/${c.clubId}`} className="card event-card">
                <h3>{c.name}</h3>
                {c.myRole && <span className="pill">{c.myRole}</span>}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** /manage/:clubId -- that club's events, upcoming first, then past. */
export function ManageClubEventsPage() {
  const { clubId = "" } = useParams();
  const [club, setClub] = useState<ClubRow | null>(null);
  const [events, setEvents] = useState<EventRow[] | null>(null);
  const [myRole, setMyRole] = useState<ClubRow["myRole"]>();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listMyClubs()
      .then((res) => setMyRole(res.clubs.find((c) => c.clubId === clubId)?.myRole))
      .catch(() => setMyRole(undefined));
  }, [clubId]);

  const classOptions = useClassOptions();
  const [trackNames, setTrackNames] = useState<string[]>([]);
  const [createForm, setCreateForm] = useState<Partial<EventRow>>({ status: "draft", classes: [] });
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<EventRow>>({});
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  function refresh() {
    setError(null);
    listClubEvents(clubId)
      .then((res) => {
        setClub(res.club);
        setEvents(res.events);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load events."));
  }
  useEffect(refresh, [clubId]);

  useEffect(() => {
    listManageTracks(clubId)
      .then((res) => setTrackNames(res.tracks.map((t) => t.name)))
      .catch(() => setTrackNames([]));
  }, [clubId]);

  // Only the fields a manager may set -- featured/clubId are service-site decisions.
  function toInput(form: Partial<EventRow>, editing: boolean): ManageEventInput {
    const c = cleanEventForm(form, editing);
    return {
      title: c.title,
      description: c.description,
      dateISO: c.dateISO,
      endDateISO: c.endDateISO,
      location: c.location,
      classes: c.classes,
      entryFeeCents: c.entryFeeCents,
      registrationOpensAt: c.registrationOpensAt,
      registrationClosesAt: c.registrationClosesAt,
      status: c.status,
    };
  }

  async function run(action: () => Promise<string>) {
    setBusy(true);
    setActionError(null);
    setInfo(null);
    try {
      setInfo(await action());
      refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    run(async () => {
      const res = await createManageEvent(clubId, toInput(createForm, false));
      setCreateForm({ status: "draft", classes: [] });
      setShowCreate(false);
      return `Created "${res.event.title}".`;
    });
  }

  function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    if (!editingId) return;
    run(async () => {
      const res = await updateManageEvent(clubId, editingId, toInput(editForm, true));
      setEditingId(null);
      return `Updated "${res.event.title}".`;
    });
  }

  function handleDelete(ev: EventRow) {
    if (!confirm(`Delete "${ev.title}"? This cannot be undone.`)) return;
    run(async () => {
      await deleteManageEvent(clubId, ev.eventId);
      if (editingId === ev.eventId) setEditingId(null);
      return `Deleted "${ev.title}".`;
    });
  }

  function startEdit(ev: EventRow) {
    setEditingId(ev.eventId);
    setEditForm({ ...ev });
    setShowCreate(false);
    setInfo(null);
  }

  if (error) return <p className="err">{error}</p>;
  if (!club || !events) return <p>Loading…</p>;

  const now = new Date().toISOString();
  const upcoming = events
    .filter((e) => (e.endDateISO ?? e.dateISO) >= now)
    .sort((a, b) => a.dateISO.localeCompare(b.dateISO));
  const past = events.filter((e) => (e.endDateISO ?? e.dateISO) < now);

  return (
    <div>
      <ManageClubNav club={club} />
      <h1>{club.name} events</h1>
      {actionError && <p className="err">{actionError}</p>}
      {info && <Toast message={info} onDismiss={() => setInfo(null)} />}
      {!showCreate && !editingId && (
        <button type="button" onClick={() => setShowCreate(true)}>+ Add event</button>
      )}
      {showCreate && (
        <EventForm
          idPrefix="mce"
          heading="New event"
          submitLabel="Create event"
          editing={false}
          value={createForm}
          onChange={setCreateForm}
          onSubmit={handleCreate}
          onCancel={() => setShowCreate(false)}
          busy={busy}
          trackNames={trackNames}
          freeTextLocation
          classOptions={classOptions}
        />
      )}
      {editingId && (
        <EventForm
          idPrefix="mee"
          heading="Edit event"
          submitLabel="Save changes"
          editing
          value={editForm}
          onChange={setEditForm}
          onSubmit={handleUpdate}
          onCancel={() => setEditingId(null)}
          busy={busy}
          trackNames={trackNames}
          freeTextLocation
          classOptions={classOptions}
        />
      )}
      {events.length === 0 && <p className="notice">This club has no events yet.</p>}
      {upcoming.length > 0 && (
        <EventTable title="Upcoming" events={upcoming} clubId={club.clubId} onEdit={startEdit} onDelete={handleDelete} />
      )}
      {past.length > 0 && (
        <EventTable title="Past" events={past} clubId={club.clubId} onEdit={startEdit} onDelete={handleDelete} />
      )}
      <ManagersPanel club={club} canEdit={myRole === "owner" || myRole === "admin"} />
      <ManageCreditsPanel club={club} events={events} />
    </div>
  );
}

/** Who manages this club. Every manager can see the list; owners (and site
 *  admins) can add people by email and remove them. */
function ManagersPanel({ club, canEdit }: { club: ClubRow; canEdit: boolean }) {
  const [managers, setManagers] = useState<ClubManagerRow[] | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<ClubRole>("manager");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  function refresh() {
    listManageClubManagers(club.clubId)
      .then((res) => setManagers(res.managers))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load managers."));
  }
  useEffect(refresh, [club.clubId]);

  async function run(action: () => Promise<string>) {
    setError(null);
    setInfo(null);
    try {
      setInfo(await action());
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    run(async () => {
      await addManageClubManager(club.clubId, email, role);
      const who = email;
      setEmail("");
      return `${who} is now ${role === "owner" ? "an owner" : "a manager"} of ${club.name}.`;
    });
  }

  return (
    <>
      <h2>Managers</h2>
      {error && <p className="err">{error}</p>}
      {info && <Toast message={info} onDismiss={() => setInfo(null)} />}
      {managers && managers.length === 0 && <p className="muted">No managers yet.</p>}
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {managers?.map((m) => (
          <li key={m.userId} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {m.email} <span className="pill">{m.role ?? "manager"}</span>
            {canEdit && (
              <button
                type="button"
                className="link"
                title={`Remove ${m.email}`}
                aria-label={`Remove ${m.email}`}
                onClick={() =>
                  confirm(`Remove ${m.email} from ${club.name}?`) &&
                  run(async () => {
                    await removeManageClubManager(club.clubId, m.userId);
                    return `${m.email} no longer manages ${club.name}.`;
                  })
                }
              >
                <Trash3FillIcon />
              </button>
            )}
          </li>
        ))}
      </ul>
      {canEdit && (
        <form onSubmit={handleAdd} className="row" style={{ marginTop: "0.5rem" }}>
          <input
            type="email"
            placeholder="Their account email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <select value={role} onChange={(e) => setRole(e.target.value as ClubRole)} style={{ flex: "0 0 auto" }}>
            <option value="manager">Manager</option>
            <option value="owner">Owner</option>
          </select>
          <button type="submit" style={{ flex: "0 0 auto" }}>Add</button>
        </form>
      )}
    </>
  );
}

function EventTable({
  title,
  events,
  clubId,
  onEdit,
  onDelete,
}: {
  title: string;
  events: EventRow[];
  clubId: string;
  onEdit: (ev: EventRow) => void;
  onDelete: (ev: EventRow) => void;
}) {
  return (
    <>
      <h2>{title}</h2>
      <div className="table-scroll"><table>
        <thead>
          <tr><th>Event</th><th>Date</th><th>Location</th><th>Status</th><th aria-label="Actions" /></tr>
        </thead>
        <tbody>
          {events.map((ev) => (
            <tr key={ev.eventId}>
              <td><Link to={`/events/${ev.eventId}`}>{ev.title}</Link></td>
              <td>{formatEventDate(ev.dateISO, ev.endDateISO)}</td>
              <td>{ev.location ?? ""}</td>
              <td>
                <span className={`pill ${ev.status === "published" ? "success" : "warn"}`}>{ev.status}</span>
              </td>
              <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                <Link to={`/manage/${clubId}/events/${ev.eventId}`} className="btn">Manage</Link>
                <button type="button" className="secondary" style={{ marginLeft: 8 }} onClick={() => onEdit(ev)}>Edit</button>
                <button type="button" className="danger" style={{ marginLeft: 8 }} onClick={() => onDelete(ev)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>
    </>
  );
}
